import React, { useEffect, useState } from "react";
import SettingsLayout from "@/components/SettingsLayout";
import api, { formatApiError } from "@/lib/api";
import { Eye, Plus, Trash2, AlertOctagon } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export default function SettingsWatchlist() {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", reason: "", expires_at: "", match_type: "exact",
  });

  const refresh = async () => {
    try {
      const { data } = await api.get("/watchlist", { params: { include_inactive: includeInactive } });
      setEntries(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { refresh(); }, [includeInactive]);

  const onCreate = async () => {
    if (!(form.name || form.email || form.phone)) return toast.error("At least one of name/email/phone");
    if (form.reason.length < 20) return toast.error("Reason must be ≥ 20 characters");
    try {
      await api.post("/watchlist", {
        ...form,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      });
      toast.success("Entry added");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", reason: "", expires_at: "", match_type: "exact" });
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this watchlist entry?")) return;
    try {
      await api.delete(`/watchlist/${id}`);
      toast.success("Deleted");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <SettingsLayout>
      <div className="space-y-6" data-testid="watchlist-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-navy-900 tracking-tight flex items-center gap-2">
              <Eye className="w-7 h-7 text-accent" /> Watchlist
            </h1>
            <p className="text-sm text-navy-500 mt-1">
              Visitors flagged here are blocked discreetly at the kiosk ("Please see reception"). Reason is never shown to the kiosk.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-navy-600 flex items-center gap-1">
              <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} className="accent-accent" />
              Show inactive
            </label>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-accent hover:bg-accent/90 text-white" data-testid="new-watchlist-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> New entry
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New watchlist entry</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <input className="w-full px-3 py-2 border border-navy-200 rounded-lg" placeholder="Name (optional)"
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="wl-name" />
                  <input className="w-full px-3 py-2 border border-navy-200 rounded-lg" placeholder="Email (optional)"
                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="wl-email" />
                  <input className="w-full px-3 py-2 border border-navy-200 rounded-lg" placeholder="Phone (optional)"
                    value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="wl-phone" />
                  <select className="w-full px-3 py-2 border border-navy-200 rounded-lg" value={form.match_type}
                    onChange={(e) => setForm({ ...form, match_type: e.target.value })} data-testid="wl-match-type">
                    <option value="exact">Exact match (case-insensitive)</option>
                    <option value="fuzzy_name">Fuzzy name match (≤ 2 character edits) — use carefully</option>
                  </select>
                  <textarea rows={3} className="w-full px-3 py-2 border border-navy-200 rounded-lg"
                    placeholder="Reason (≥ 20 chars, internal only)"
                    value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="wl-reason" />
                  <input type="date" className="w-full px-3 py-2 border border-navy-200 rounded-lg"
                    value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="wl-expires-at"
                    placeholder="Expires (optional)" />
                </div>
                <DialogFooter>
                  <Button onClick={onCreate} className="bg-accent hover:bg-accent/90 text-white" data-testid="wl-submit">Add to watchlist</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {entries.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-navy-200 p-10 text-center text-navy-500">
            No watchlist entries.
          </div>
        ) : (
          <div className="rounded-2xl bg-white border border-navy-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-navy-50 text-navy-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Name / Email / Phone</th>
                  <th className="px-4 py-3 text-left">Match</th>
                  <th className="px-4 py-3 text-left">Reason</th>
                  <th className="px-4 py-3 text-left">Expires</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-100">
                {entries.map((e) => (
                  <tr key={e.id} data-testid={`wl-row-${e.id}`}>
                    <td className="px-4 py-3 text-navy-900">
                      {e.name && <div className="font-medium">{e.name}</div>}
                      {e.email && <div className="text-xs text-navy-500">{e.email}</div>}
                      {e.phone && <div className="text-xs text-navy-500">{e.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <span className="font-mono px-1.5 py-0.5 rounded bg-navy-50 text-navy-700">{e.match_type}</span>
                    </td>
                    <td className="px-4 py-3 text-navy-700 max-w-md truncate" title={e.reason}>{e.reason}</td>
                    <td className="px-4 py-3 text-navy-600 text-xs">{e.expires_at ? new Date(e.expires_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                        e.active ? "bg-success-50 text-success-700" : "bg-navy-50 text-navy-500"
                      }`}>{e.active ? "Active" : "Inactive"}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="icon" onClick={() => remove(e.id)} data-testid={`wl-delete-${e.id}`}>
                        <Trash2 className="w-4 h-4 text-danger-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SettingsLayout>
  );
}
